/**
 * Dynamic Add-on Cart Manager for Shopify Basic
 * Uses product tags to manage relationships - No hardcoded mapping!
 * Fixed for Dawn theme compatibility
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  
  const CONFIG = {
    // Tag system for identifying products
    tags: {
      addonTrigger: 'addon-trigger',    // Tag on main products that can have add-ons
      isAddon: 'is-addon',              // Tag on add-on products
      groupPrefix: 'addon-group-'       // Prefix for group tags (e.g., 'addon-group-electronics')
    },
    
    // Cache duration in milliseconds (5 minutes)
    cacheDuration: 5 * 60 * 1000,
    
    // Debug mode - set to true to see console logs
    debug: true
  };

  // Prevent infinite reload loop
  const REMOVAL_FLAG = 'addon_removal_attempted';
  
  function shouldAttemptRemoval() {
    const lastAttempt = sessionStorage.getItem(REMOVAL_FLAG);
    if (!lastAttempt) return true;
    
    const timeSinceLastAttempt = Date.now() - parseInt(lastAttempt);
    // Only attempt once per 2 seconds
    return timeSinceLastAttempt > 2000;
  }
  
  function markRemovalAttempted() {
    sessionStorage.setItem(REMOVAL_FLAG, Date.now().toString());
  }
  
  function clearRemovalFlag() {
    sessionStorage.removeItem(REMOVAL_FLAG);
  }

  // ============================================
  // CACHE MANAGEMENT
  // ============================================

  const cache = {
    products: {},
    timestamp: null
  };

  function isCacheValid() {
    if (!cache.timestamp) return false;
    return (Date.now() - cache.timestamp) < CONFIG.cacheDuration;
  }

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  function log(...args) {
    if (CONFIG.debug) {
      console.log('[Dynamic Addon Manager]', ...args);
    }
  }

  function showNotification(message, type = 'notice') {
    // Create a simple notification
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #333;
      color: white;
      padding: 15px 20px;
      border-radius: 4px;
      z-index: 10000;
      max-width: 300px;
      box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    `;
    notification.textContent = message;
    document.body.appendChild(notification);
    
    setTimeout(() => {
      notification.remove();
    }, 3000);
  }

  // ============================================
  // PRODUCT DATA FETCHING - FIXED VERSION
  // ============================================

  async function fetchProductByHandle(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      if (!response.ok) throw new Error('Product not found');
      return await response.json();
    } catch (error) {
      log('Error fetching product by handle:', error);
      return null;
    }
  }

  async function fetchProductData(variantId) {
    // Check cache first
    if (isCacheValid() && cache.products[variantId]) {
      log('Returning cached product data for variant:', variantId);
      return cache.products[variantId];
    }

    try {
      // First, get the cart to find the product handle
      const cartResponse = await fetch('/cart.js');
      const cart = await cartResponse.json();
      
      // Find the cart item with this variant
      const cartItem = cart.items.find(item => item.variant_id === variantId);
      
      if (cartItem && cartItem.handle) {
        // Fetch full product data using handle
        const productData = await fetchProductByHandle(cartItem.handle);
        
        if (productData) {
          // Cache the result
          cache.products[variantId] = {
            id: productData.id,
            handle: productData.handle,
            title: productData.title,
            tags: productData.tags || [],
            variants: productData.variants
          };
          
          cache.timestamp = Date.now();
          
          log('Fetched product data:', cache.products[variantId]);
          return cache.products[variantId];
        }
      }
      
      // Fallback: try to extract from page if we're on a product page
      if (window.location.pathname.includes('/products/')) {
        const productHandle = window.location.pathname.split('/products/')[1].split('?')[0];
        const productData = await fetchProductByHandle(productHandle);
        
        if (productData) {
          // Cache all variants
          productData.variants.forEach(variant => {
            cache.products[variant.id] = {
              id: productData.id,
              handle: productData.handle,
              title: productData.title,
              tags: productData.tags || [],
              variants: productData.variants
            };
          });
          
          cache.timestamp = Date.now();
          return cache.products[variantId];
        }
      }
      
      return null;
      
    } catch (error) {
      log('Error fetching product data:', error);
      return null;
    }
  }

  async function getCartWithProductData(cart) {
    const itemsWithData = await Promise.all(
      cart.items.map(async (item) => {
        // Use handle from cart item if available
        let productData = null;
        
        if (item.handle) {
          productData = await fetchProductByHandle(item.handle);
          if (productData) {
            productData = {
              id: productData.id,
              handle: productData.handle,
              title: productData.title,
              tags: productData.tags || [],
              variants: productData.variants
            };
          }
        }
        
        // Fallback to cached data
        if (!productData) {
          productData = cache.products[item.variant_id] || null;
        }
        
        return {
          ...item,
          productData: productData
        };
      })
    );
    
    return {
      ...cart,
      items: itemsWithData
    };
  }

  // ============================================
  // TAG-BASED LOGIC
  // ============================================

  function getAddonGroups(tags) {
    return tags
      .filter(tag => tag.startsWith(CONFIG.tags.groupPrefix))
      .map(tag => tag);
  }

  function isMainProduct(tags) {
    return tags.includes(CONFIG.tags.addonTrigger);
  }

  function isAddonProduct(tags) {
    return tags.includes(CONFIG.tags.isAddon);
  }

  function getActiveAddonGroups(cartWithData) {
    const activeGroups = new Set();
    
    cartWithData.items.forEach(item => {
      if (!item.productData) return;
      
      const tags = item.productData.tags;
      if (isMainProduct(tags)) {
        const groups = getAddonGroups(tags);
        groups.forEach(group => activeGroups.add(group));
      }
    });
    
    return Array.from(activeGroups);
  }

  function shouldAddonBeInCart(addonTags, activeGroups) {
    if (!isAddonProduct(addonTags)) return true; // Not an add-on
    
    const addonGroups = getAddonGroups(addonTags);
    
    // Check if any of the add-on's groups are active
    return addonGroups.some(group => activeGroups.includes(group));
  }

  // ============================================
  // CART OPERATIONS
  // ============================================

  async function getCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (error) {
      log('Error fetching cart:', error);
      return null;
    }
  }

  async function updateCartItem(lineItemKey, quantity) {
    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id: lineItemKey,
          quantity: quantity
        })
      });
      const result = await response.json();
      log('Cart update result:', result);
      return result;
    } catch (error) {
      log('Error updating cart:', error);
      return null;
    }
  }

  // ============================================
  // MAIN LOGIC
  // ============================================

  async function removeInvalidAddons() {
    // Prevent infinite loop
    if (!shouldAttemptRemoval()) {
      log('Skipping removal attempt - too soon after last attempt');
      return false;
    }
    
    const cart = await getCart();
    if (!cart || cart.items.length === 0) {
      log('Cart is empty or unavailable');
      clearRemovalFlag();
      return false;
    }

    log('Checking cart for invalid add-ons...');
    
    const cartWithData = await getCartWithProductData(cart);
    const activeGroups = getActiveAddonGroups(cartWithData);
    
    log('Active addon groups:', activeGroups);
    log('Cart items:', cartWithData.items.map(i => ({
      title: i.title,
      key: i.key,
      variant_id: i.variant_id,
      tags: i.productData?.tags
    })));

    const invalidAddons = cartWithData.items.filter(item => {
      if (!item.productData) {
        log('No product data for item:', item.title);
        return false;
      }
      
      const tags = item.productData.tags;
      const shouldStay = shouldAddonBeInCart(tags, activeGroups);
      
      if (!shouldStay) {
        log('Invalid add-on found:', item.title, 'with tags:', tags);
      }
      
      return !shouldStay;
    });

    if (invalidAddons.length === 0) {
      log('No invalid add-ons to remove');
      clearRemovalFlag();
      return false;
    }

    log('Removing invalid add-ons:', invalidAddons.map(a => ({
      title: a.title,
      key: a.key,
      variant_id: a.variant_id
    })));

    markRemovalAttempted();

    let allRemoved = true;
    for (const addon of invalidAddons) {
      // Use the line item key (not variant_id) for removal
      const result = await updateCartItem(addon.key, 0);
      if (!result) {
        log('Failed to remove:', addon.title);
        allRemoved = false;
      } else {
        log('Successfully removed:', addon.title);
      }
    }

    if (allRemoved) {
      showNotification('Add-on items were removed because their main product is no longer in your cart.');
      clearRemovalFlag();
      // Wait a bit before reloading to ensure the cart updates are processed
      setTimeout(() => {
        window.location.reload();
      }, 500);
      return true;
    } else {
      log('Some items failed to remove - check tags on products');
      return false;
    }
  }

  async function validateAddonBeforeAdd(variantId) {
    // Try to get product data from current page first
    if (window.location.pathname.includes('/products/')) {
      const productHandle = window.location.pathname.split('/products/')[1].split('?')[0];
      const productData = await fetchProductByHandle(productHandle);
      
      if (productData) {
        const variant = productData.variants.find(v => v.id === parseInt(variantId));
        if (variant) {
          const tags = productData.tags || [];
          
          // If it's not an add-on, allow it
          if (!isAddonProduct(tags)) {
            log('Not an add-on, allowing:', productData.title);
            return true;
          }
          
          // Get current cart and check active groups
          const cart = await getCart();
          if (!cart) return false;
          
          const cartWithData = await getCartWithProductData(cart);
          const activeGroups = getActiveAddonGroups(cartWithData);
          
          log('Validating add-on:', productData.title);
          log('Active groups:', activeGroups);
          log('Add-on groups:', getAddonGroups(tags));
          
          const isAllowed = shouldAddonBeInCart(tags, activeGroups);
          
          if (!isAllowed) {
            const requiredGroups = getAddonGroups(tags)
              .map(g => g.replace(CONFIG.tags.groupPrefix, ''))
              .join(', ');
            showNotification(`Please add a qualifying main product first to add this item. (Required: ${requiredGroups})`);
          }
          
          return isAllowed;
        }
      }
    }
    
    // If we can't determine, allow it (fail open)
    return true;
  }

  // ============================================
  // UI HELPERS
  // ============================================

  async function updateAddonVisibility() {
    const cart = await getCart();
    if (!cart) return;

    const cartWithData = await getCartWithProductData(cart);
    const activeGroups = getActiveAddonGroups(cartWithData);

    log('Updating addon visibility. Active groups:', activeGroups);

    // Update sections with data-addon-group attribute
    document.querySelectorAll('[data-addon-group]').forEach(section => {
      const requiredGroup = CONFIG.tags.groupPrefix + section.getAttribute('data-addon-group');
      
      if (activeGroups.includes(requiredGroup)) {
        section.style.display = '';
        section.classList.add('addon-available');
      } else {
        section.style.display = 'none';
        section.classList.remove('addon-available');
      }
    });

    // Dispatch custom event
    document.dispatchEvent(new CustomEvent('addons:visibility-updated', {
      detail: { activeGroups }
    }));
  }



  // ============================================
  // CART CHANGE DETECTION
  // ============================================

  function setupCartChangeListeners() {
    // Listen for Dawn theme cart events
    document.addEventListener('cart:updated', async () => {
      log('Cart updated event detected');
      await updateAddonVisibility();
      
      // On cart page, check for invalid add-ons
      if (window.location.pathname.includes('/cart')) {
        setTimeout(async () => {
          await removeInvalidAddons();
        }, 300);
      }
    });

    // Listen for variant changes on product pages
    document.addEventListener('variant:change', async () => {
      log('Variant change detected');
      await updateAddonVisibility();
    });

    // Intercept cart drawer opens/closes
    const cartDrawerObserver = new MutationObserver(async (mutations) => {
      for (const mutation of mutations) {
        if (mutation.attributeName === 'class' || mutation.attributeName === 'open') {
          log('Cart drawer changed');
          await updateAddonVisibility();
        }
      }
    });

    // Observe cart drawer if it exists
    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer) {
      cartDrawerObserver.observe(cartDrawer, { 
        attributes: true,
        attributeFilter: ['class', 'open']
      });
    }

    // Monitor cart item quantity changes
    document.addEventListener('change', async (e) => {
      if (e.target.matches('input[name="updates[]"]') || 
          e.target.matches('.quantity__input')) {
        log('Cart quantity changed');
        setTimeout(async () => {
          await removeInvalidAddons();
        }, 500);
      }
    });

    // Monitor cart remove buttons
    document.addEventListener('click', async (e) => {
      if (e.target.closest('.cart__remove') || 
          e.target.closest('[name="update"]') ||
          e.target.closest('cart-remove-button')) {
        log('Cart remove button clicked');
        setTimeout(async () => {
          if (window.location.pathname.includes('/cart')) {
            await removeInvalidAddons();
          }
        }, 500);
      }
    });
  }

  // Override fetch to detect cart API calls
  function setupFetchInterception() {
    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = args[0];
      
      // Detect cart-related API calls
      if (typeof url === 'string' && 
          (url.includes('/cart/add') || 
           url.includes('/cart/change') || 
           url.includes('/cart/update'))) {
        
        return originalFetch.apply(this, args).then(async response => {
          // Clone response so we can still return it
          const clonedResponse = response.clone();
          
          log('Cart API call detected:', url);
          
          // Update visibility after cart changes
          setTimeout(async () => {
            await updateAddonVisibility();
            
            if (window.location.pathname.includes('/cart')) {
              await removeInvalidAddons();
            }
          }, 300);
          
          return clonedResponse;
        });
      }
      
      // Handle add-on validation for add to cart
      if (typeof url === 'string' && url.includes('/cart/add')) {
        const options = args[1];
        if (options && options.body) {
          try {
            const body = JSON.parse(options.body);
            const variantId = body.id || (body.items?.[0]?.id);
            
            if (variantId) {
              return validateAddonBeforeAdd(variantId).then(isValid => {
                if (isValid) {
                  return originalFetch.apply(this, args);
                } else {
                  return Promise.reject(new Error('Add-on requires main product'));
                }
              });
            }
          } catch (e) {
            log('Error parsing fetch body:', e);
          }
        }
      }
      
      return originalFetch.apply(this, args);
    };
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  async function init() {
    log('Initializing Dynamic Addon Cart Manager');
    log('Configuration:', CONFIG.tags);

    // Setup fetch interception first
    setupFetchInterception();

    // Remove invalid add-ons on cart page
    if (window.location.pathname.includes('/cart')) {
      // Add a small delay to ensure cart is fully loaded
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const removed = await removeInvalidAddons();
      if (removed) {
        // Exit early - page will reload
        return;
      }
    }

    // Update add-on visibility
    await updateAddonVisibility();

    // Setup all cart change listeners
    setupCartChangeListeners();

    // Intercept form submissions for add to cart
    document.addEventListener('submit', async function(e) {
      const form = e.target;
      
      if (form.getAttribute('action')?.includes('/cart/add') || 
          form.querySelector('[name="id"]')) {
        
        const variantIdInput = form.querySelector('[name="id"]');
        if (!variantIdInput) return;

        const variantId = variantIdInput.value;
        const isValid = await validateAddonBeforeAdd(variantId);
        
        if (!isValid) {
          e.preventDefault();
          return false;
        }
        
        // After successful form submission, update visibility
        setTimeout(async () => {
          await updateAddonVisibility();
        }, 500);
      }
    });
    
    log('Dynamic Addon Cart Manager initialized');
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose API
  window.AddonCartManager = {
    removeInvalidAddons,
    updateAddonVisibility,
    validateAddonBeforeAdd,
    config: CONFIG
  };

})();